import React, { useState, useEffect } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import { Auth, Hub } from "aws-amplify";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import AuthenticatedLayout from "classic/layouts/AuthenticatedLayout.js";
import StartPageLayout from "classic/layouts/StartPage.js";
import RegisterPageLayout from "classic/layouts/RegisterPage.js";
import PasswordResetLayout from "classic/layouts/PasswordResetPage.js";
import MicrosoftAuthLayout from "classic/layouts/MicrosoftAuthLayout";
import Atl from "classic/views/Atl";
import { TourProvider } from "@reactour/tour";
import { BrowserRouter } from "react-router-dom";
import {
  setUserVault,
  invokeLambdaWithRetry,
  fetchJSONFromS3,
} from "services/Helper";
import { getParameterFromManager } from "./services/Utils";
import ReloadTimerUtils from "../src/classic/components/Utils/ReloadTimerUtils";
import "./App.css";
import NewUiRoutes from "newui/components/NewUiRoutes";
import { userTracer, UserTracerRouteTracker } from "./logging";
import { createUiLogger } from "./logging/uiLogger";
import { LEGACY_MEA_OPS_ROUTES, MEA_OPS_ROUTES } from "./meaOpsRoutes";
import {
  clearModuleNavigationState,
  navigateFromPlatform,
  readModuleNavigationState,
} from "./appNavigation";
import {
  clearIncompleteMfaState,
  isAuthBootstrapPending,
} from "./authBootstrap";
import { buildClassicQaToolPath } from "./classicQaToolNavigation";
const console = createUiLogger("meaplatform/App.js");

const ATL_GROUP = "MEA_AUTOMATION_RESTRICTED";

const isAutomationRestrictedUser = (user) => (
  user?.signInUserSession?.accessToken?.payload?.["cognito:groups"] || []
).includes(ATL_GROUP);

const getLandingPageForUser = (user, menuItems) => {
  const groups = user?.signInUserSession?.idToken?.payload?.["cognito:groups"] || [];
  if (groups.includes("MEA_UNDERWRITER") || groups.includes("MEA_FINANCE")) {
    return MEA_OPS_ROUTES.PORTFOLIO;
  }

  return menuItems && Object.keys(menuItems).length > 0 &&
    menuItems.Ingestion?.items?.find((item) => item.title === "Home")?.enabled
    ? "/user/home"
    : "/user/submission-summary";
};

const LegacyMeaOpsRedirect = ({ to }) => {
  const location = useLocation();
  useEffect(() => {
    navigateFromPlatform(
      undefined,
      `${to}${location.search}${location.hash}`,
      { replace: true },
    );
  }, [location.hash, location.search, to]);
  return null;
};

// Sends /ops routes to the separately deployed MEA Ops application.
const RedirectToMeaOps = ({ path }) => {
  useEffect(() => {
    navigateFromPlatform(undefined, path, { replace: true });
  }, [path]);
  return null;
};

const ClassicQaToolRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const navigationState = readModuleNavigationState(location.pathname);
    if (navigationState) {
      clearModuleNavigationState();
    }
    navigate(buildClassicQaToolPath(navigationState), { replace: true });
  }, [location.pathname, navigate]);

  return null;
};



function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authBootstrapResolved, setAuthBootstrapResolved] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [statusMsg, setStatusMsg] = useState();
  const [prevBuildValue, setPrevBuildValue] = useState("");
  const [currentBuildValue, setCurrentBuildValue] = useState("");
  const [showReloadModal, setShowReloadModal] = useState(false);
  const [showNoReloadModal, setShowNoReloadModal] = useState(false);
  const [showUiTimerModal, setShowUiTimerModal] = useState(false);
  const [oldBuildValue, setOldBuildValue] = useState("");
  const [switchUI, setSwitchUI] = useState(null); // null until we fetch from user vault
  const [menuItems, setMenuItems] = useState({});
  const defaultAccessControl = {
    blockMobile: false,
    allowTablets: true,
    message:
      "This platform instance is not available on mobile devices. Please use a desktop browser.",
  };
  const [accessControl, setAccessControl] = useState(defaultAccessControl);
  const [uiConfigLoaded, setUiConfigLoaded] = useState(false);
  const [isAccessBlocked, setIsAccessBlocked] = useState(false);
   const [blockMessage, setBlockMessage] = useState("");

  const normalizeAccessControl = (config) => ({
    ...defaultAccessControl,
    ...(config || {}),
  });

  const getDeviceInfo = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera || "";
    const isIpadOS =
      navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
    const isTablet =
      isIpadOS ||
      /iPad|Tablet|PlayBook|Silk|Kindle|Nexus 7|Nexus 10|SM-T|GT-P|Tab/i.test(ua);
    const isPhoneUA =
      /Mobi|Android|iPhone|iPod|IEMobile|BlackBerry|Opera Mini/i.test(ua) &&
      !isTablet;
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const hasSmallScreen = window.screen.width <= 768 || window.screen.height <= 768;
    const hasOrientationAPI = typeof window.orientation !== 'undefined';
    
    const isProbablyPhone = (isPhoneUA || (hasTouchScreen && hasSmallScreen && hasOrientationAPI)) && !isTablet;
    
    return { isPhone: isProbablyPhone, isTablet };
  };

  const shouldBlockAccess = (config) => {
    if (!config?.blockMobile) return false;
    const { isPhone, isTablet } = getDeviceInfo();
    
    let blocked = false;
    let message = "";
    
    if (isPhone) {
      blocked = true;
      message = config?.message || defaultAccessControl.message;
    } else if (isTablet && !config?.allowTablets) {
      blocked = true;
      const baseMessage = config?.message || defaultAccessControl.message;
      message = baseMessage.replace(/mobile/gi, "tablet");
    }
    
    return { blocked, message };
  };

  const fetchMenuItems = async () => {
    const filePath = `system/config/uiConfiguration.json`;
    try {
      const fileData = await fetchJSONFromS3(filePath);
      setAccessControl(normalizeAccessControl(fileData?.accessControl));
      setUiConfigLoaded(true);
      return fileData?.menuItems || {};
    } catch (error) {
      console.error("Failed to fetch UI configuration:", error);
      setAccessControl(defaultAccessControl);
      setUiConfigLoaded(true);
      return {};
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (currentUser) {
        setLoading(true);
        const items = await fetchMenuItems();
        setMenuItems(items);
        setLoading(false);
      } else {
        setAccessControl(defaultAccessControl);
        setUiConfigLoaded(true);
      }
    };
    loadData();
  }, [currentUser]);

  useEffect(() => {
    if (!uiConfigLoaded) return;
    const { blocked, message } = shouldBlockAccess(accessControl);
    setIsAccessBlocked(blocked);
    setBlockMessage(message);
  }, [accessControl, uiConfigLoaded]);

  useEffect(() => {
    userTracer.setUserContext(currentUser && currentUser !== "NOTSET" ? currentUser : null);
  }, [currentUser]);

  const landingPage = getLandingPageForUser(currentUser, menuItems);
  const authBootstrapPending = isAuthBootstrapPending({
    authBootstrapResolved,
    currentUser,
  });
  const shouldRenderNewUi = switchUI !== null && switchUI !== "classic";

  useEffect(() => {
    const interval = setInterval(async () => {
      if (!showReloadModal) {
        setOldBuildValue(prevBuildValue);
        setShowUiTimerModal(false);
        let buildValueParam;
        if (
          (window.location.pathname === "/login" ||
            window.location.pathname === "/") &&
          currentUser === null
        ) {
          buildValueParam = await getBuildVersionValue();
        } else {
          buildValueParam = await getParameterFromManager(
            "MEAPLATFORM_BUILD_VERSION",
            {
              impactKind: "background",
              impactReason:
                "Periodic build-version poll for reload detection while the app is already open.",
              data: {
                pollType: "build-version-check",
              },
            }
          );
        }
        setCurrentBuildValue(buildValueParam);
        let envStatusAutoreload = import.meta.env.VITE_DISABLE_AUTORELOAD;
        if (
          envStatusAutoreload === undefined ||
          envStatusAutoreload === null ||
          envStatusAutoreload === "false"
        ) {
          if (
            prevBuildValue === undefined ||
            prevBuildValue === null ||
            prevBuildValue === buildValueParam ||
            prevBuildValue === ""
          ) {
            setShowReloadModal(false);
          } else {
            if (
              (window.location.pathname === "/login" ||
                window.location.pathname === "/") &&
              currentUser === null
            ) {
              window.location.reload();
            } else {
              setShowReloadModal(true);
            }
          }
        }
        setPrevBuildValue(buildValueParam);
      }
    }, 15 * 60 * 1000); // 15 minutes in milliseconds

    return () => clearInterval(interval); // Clean up interval on component unmount
  }, [prevBuildValue, currentUser]);

  async function getBuildVersionValue() {
    try {
      const body = {
        target: "build-version-value",
      };
      const init = {
        body: body,
      };
      const resp = await invokeLambdaWithRetry("instanceHandler", init);
      console.debug("getBuildVersionValue resp:", resp);
      return resp.response;
    } catch (err) {
      console.error("err: ", err);
      return false;
    }
  }

  useEffect(() => {
    const handleAuthEvent = async (event) => {
      if (
        event.payload.event === "signIn" ||
        event.payload.event === "verify"
      ) {
        if (import.meta.env.VITE_MFANAME === undefined) {
          // Fetch UI preference before setting current user
          if (import.meta.env.VITE_SWITCH_UI !== undefined) {
            await getUserVault(event.payload.data.attributes.email);
          } else {
            setSwitchUI("classic");
          }
          setCurrentUser(event.payload.data);
        } else if (
          import.meta.env.VITE_MFANAME !== undefined &&
          (!isAutomationRestrictedUser(event.payload.data) &&
            (
              event.payload.data.preferredMFA !== "NOMFA" ||
              localStorage.getItem("mfasetupready") === "true"
            ))
        ) {
          if (
            event.payload.data.username.startsWith("okta_") ||
            event.payload.data.username.startsWith("azuread_") ||
            event.payload.data.username.startsWith("azureadopen_")
          ) {
            //includes azureadopen
            var uInfo = event.payload.data.signInUserSession.idToken.payload;
            setUserVault(
              undefined,
              uInfo.given_name,
              uInfo.family_name,
              uInfo.phone_number,
              uInfo["custom:company"],
              uInfo["custom:country"],
              uInfo["cognito:groups"].join(","),
              uInfo.sub
            );
          } else if (localStorage.getItem("mfasetupready") === "true") {
            localStorage.removeItem("mfasetupready");
          }
          // Fetch UI preference before setting current user
          if (import.meta.env.VITE_SWITCH_UI !== undefined) {
            await getUserVault(event.payload.data.attributes.email);
          } else {
            setSwitchUI("classic");
          }
          setCurrentUser(event.payload.data);
        }
      } else if (event.payload.event === "signOut") {
        if (localStorage.getItem("transitions") !== "off") {
          setCurrentUser(null);
          setSwitchUI(null); // Reset UI preference on sign out
        }
      }
    };

    Hub.listen("auth", handleAuthEvent);

    return () => {
      Hub.remove("auth", handleAuthEvent);
    };
  }, []);

  function reqFedLogout(userAttributes) {
    const index = userAttributes.findIndex((userAttribute) => {
      return (
        userAttribute.Name === "email" &&
        userAttribute.Value.toLowerCase().endsWith("@meaplatform.com")
      );
    });
    if (index !== -1) return false;
    return true;
  }

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        if (currentUser === null) {
          setShowModal(true);
          setStatusMsg("Detecting existing session");
        }
        const user = await Auth.currentAuthenticatedUser({
          bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
        });
        // A previous abandoned MFA challenge may have left this marker in
        // localStorage. The Cognito session above is authoritative, so clear
        // the marker without signing out the valid user.
        clearIncompleteMfaState(localStorage);
        if (window.location.pathname === "/logout") {
          var body = {
            access_token: user.signInUserSession.idToken.jwtToken,
            target: "logout"
          };
          const init = {
            body: body,
          };
          await invokeLambdaWithRetry("instanceHandler", init, "Event");
          window.location.href = "/login";
          if (
            user &&
            user.storage &&
            user.storage[user.userDataKey] &&
            JSON.parse(user.storage[user.userDataKey]).UserAttributes &&
            reqFedLogout(
              JSON.parse(user.storage[user.userDataKey]).UserAttributes
            )
          ) {
            await Auth.signOut({ global: true });
          } else {
            user.getSession((err) => {
              if (err) {
                console.error("Error getting session:", err);
                return;
              }
              user.globalSignOut({
                onSuccess: (result) => {
                  console.error("Global sign-out successful:", result);
                },
                onFailure: (err) => {
                  console.error("Global sign-out failed:", err);
                },
              });
            });
          }
        } else {
          // Fetch UI preference before setting current user
          if (import.meta.env.VITE_SWITCH_UI !== undefined) {
            await getUserVault(user.attributes.email);
          } else {
            // If UI switching is not enabled, default to classic
            setSwitchUI("classic");
          }
          setCurrentUser(user);
        }
      } catch (err) {
        console.error(err);
        if (window.location.pathname === "/logout") {
          window.location.href = "/login";
        } else {
          // On error, default to classic UI
          setSwitchUI("classic");
        }
      }
      setLoading(false);
      setShowModal(false);
      setAuthBootstrapResolved(true);
    }
    fetchData();
  }, []);
  var pendingUsers = false;
  if (currentUser) {
    let data = currentUser.signInUserSession.accessToken.payload;
    if (data[`cognito:groups`]?.length !== 0) {
      if (data[`cognito:groups`]?.includes("MEA_PENDINGUSERS")) {
        pendingUsers = true;
      }
    }
  }

  async function getUserVault(email) {
    try {
      const body = {
        target: "getUserVault",
        UserId: email,
      };
      const init = {
        body: body,
      };
      const result = await invokeLambdaWithRetry("instanceHandler", init);
      const uiPreference = result?.response?.data?.getUserVault?.extraInfo;
      
      if (!uiPreference) {
        if (import.meta.env.VITE_SWITCH_UI !== undefined) {
          const defaultUI = await getDefaultUIFromValidationCheck();
          setSwitchUI(defaultUI);
        } else {
          setSwitchUI("classic");
        }
      } else {
        setSwitchUI(uiPreference);
      }
      
      return result;
    } catch (err) {
      console.error(err);
      if (import.meta.env.VITE_SWITCH_UI !== undefined) {
        const defaultUI = await getDefaultUIFromValidationCheck();
        setSwitchUI(defaultUI);
      } else {
        setSwitchUI("classic");
      }
      return false;
    }
  }

  async function getDefaultUIFromValidationCheck() {
    try {
      const validationCheck = await getParameterFromManager(
        "MEA_VALIDATIONS_CHECK"
      );
      if (validationCheck) {
        const showUI = validationCheck?.default?.showUI;
        return showUI;
      }
      return "classic";
    } catch (err) {
      console.error("Error fetching MEA_VALIDATIONS_CHECK:", err);
      return "classic";
    }
  }

  const toggleSwitchUI = async () => {
    const newMode = switchUI === "classic" ? "newui" : "classic";
    const persisted = await setUserVault(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      newMode,
    );
    if (persisted) {
      setSwitchUI(newMode);
    }
    return Boolean(persisted);
  };

  useEffect(() => {
    const logoutListener = (event) => {
      if (event.key === "logout") {
        localStorage.removeItem("logout");
        window.location.href = "/login";
      }
    };

    window.addEventListener("storage", logoutListener);

    return () => {
      window.removeEventListener("storage", logoutListener);
    };
  }, []);

  if (uiConfigLoaded && isAccessBlocked) {
    return (
      <div className="access-blocked">
        <div className="access-blocked__card">
          <h2>Access blocked</h2>
          <p>{blockMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <BrowserRouter>
        <UserTracerRouteTracker />
        <TourProvider
          onClickMask={(e) => e.preventDefault()}
          prevButton={({ currentStep, setCurrentStep, steps }) => {
            const first = currentStep === 0;
            return (
              <button
                className="backbtn"
                onClick={() => {
                  if (first) {
                    setCurrentStep(0);
                  } else {
                    setCurrentStep((s) => s - 1);
                  }
                }}
              >
                Back
              </button>
            );
          }}
          nextButton={({
            Button,
            currentStep,
            stepsLength,
            setIsOpen,
            setCurrentStep,
            steps,
          }) => {
            const last = currentStep === stepsLength - 1;
            return (
              <button
                className="backbtn"
                onClick={() => {
                  if (last) {
                    setIsOpen(false);
                  } else {
                    setCurrentStep((s) =>
                      s === steps?.length - 1 ? 0 : s + 1
                    );
                  }
                }}
              >
                {last ? "Done!" : "Next"}
              </button>
            );
          }}
        >
          <React.StrictMode>
            {!authBootstrapPending && <Routes>
              {!currentUser && <Route path="/atl" element={<Atl />} />}
              {!currentUser && <Route path="/atl/" element={<Atl />} />}
              {(!currentUser || pendingUsers) && (
                <Route
                  path="/login"
                  element={<StartPageLayout pendingUser={pendingUsers} />}
                />
              )}
              {!currentUser && (
                <Route path="/register" element={<RegisterPageLayout />} />
              )}
              {!currentUser && (
                <Route path="/pwdreset" element={<PasswordResetLayout />} />
              )}
              {!currentUser && (
                <Route
                  path="/microsoftauth"
                  element={<MicrosoftAuthLayout />}
                />
              )}
              {!currentUser && (
                <Route
                  path="/"
                  element={<StartPageLayout switchUI={switchUI} />}
                />
              )}
              {!currentUser && (
                <Route
                  path="/user/*"
                  element={<StartPageLayout switchUI={switchUI} />}
                />
              )}
              {!currentUser && (
                <Route
                  path="/*"
                  element={<StartPageLayout switchUI={switchUI || "classic"} />}
                />
              )}

              {currentUser && switchUI === "classic" && (
                <Route
                  path="/user/qa-tool"
                  element={<ClassicQaToolRedirect />}
                />
              )}

              {currentUser && switchUI === "classic" && (
                <Route
                  path="/user/submission-summary"
                  element={<Navigate to="/user/submissions" replace />}
                />
              )}

              {currentUser &&
                switchUI !== null &&
                switchUI === "classic" && (
                <Route
                  path="/*"
                  element={
                    <AuthenticatedLayout toggleSwitchUI={toggleSwitchUI} />
                  }
                />
              )}
              
              {currentUser && shouldRenderNewUi && (
                <Route
                  path="/*"
                  element={<NewUiRoutes toggleSwitchUI={toggleSwitchUI} />}
                />
              )}

              {currentUser && shouldRenderNewUi && (
                <>
                  <Route
                    path={LEGACY_MEA_OPS_ROUTES.TRIAGE}
                    element={
                      <LegacyMeaOpsRedirect to={MEA_OPS_ROUTES.TRIAGE} />
                    }
                  />
                  <Route
                    path={LEGACY_MEA_OPS_ROUTES.PORTFOLIO}
                    element={
                      <LegacyMeaOpsRedirect to={MEA_OPS_ROUTES.PORTFOLIO} />
                    }
                  />
                </>
              )}

              {currentUser && shouldRenderNewUi && (
                <Route
                  path="/user/submissions"
                  element={
                    landingPage === MEA_OPS_ROUTES.PORTFOLIO ? (
                      <RedirectToMeaOps path={landingPage} />
                    ) : (
                      <Navigate to={landingPage} />
                    )
                  }
                />
              )}

              {currentUser && currentUser !== "NOTSET" && (
                <Route
                  path="/user"
                  element={
                    switchUI === "classic" ? (
                      <Navigate to="/user/submissions" />
                    ) : landingPage === MEA_OPS_ROUTES.PORTFOLIO ? (
                      <RedirectToMeaOps path={landingPage} />
                    ) : (
                      <Navigate to={landingPage} />
                    )
                  }
                />
              )}
              {currentUser && currentUser !== "NOTSET" && (
                <Route
                  path="/login"
                  element={
                    <Navigate
                      to={!pendingUsers ? "/user/submissions" : "/login"}
                    />
                  }
                />
              )}
              {currentUser && currentUser !== "NOTSET" && (
                <Route path="/" element={<Navigate to="/user/submissions" />} />
              )}
            </Routes>}
          </React.StrictMode>
        </TourProvider>
      </BrowserRouter>

      {showUiTimerModal && (
        <ReloadTimerUtils
          showUiTimerModal={showUiTimerModal}
          setShowUiTimerModal={setShowUiTimerModal}
        />
      )}

      {isLoading && (
        <div className="existingsession">
          <Modal
            className="modal-md modal-primary"
            show={showModal}
            onHide={() => setShowModal(false)}
            backdrop="static"
            centered
            animation={false}
          >
            <Modal.Body className="text-center">
              <p>{statusMsg}</p>
            </Modal.Body>
          </Modal>
        </div>
      )}

      <Modal
        className="modal-md modal-primary preview-detailed-summary"
        show={showReloadModal}
        onHide={() => setShowReloadModal(false)}
        backdrop="static"
        centered
        animation={false}
        backdropClassName="question-modal"
      >
        <Modal.Title className="modal-title modal-header mt-0 mb-3 summary-border-header">
          <Row className="w-100 pb-2">
            <Col className="col-12 text-center">
              <h3>
                <b>Attention !!!</b>
              </h3>
            </Col>
          </Row>
        </Modal.Title>
        <Modal.Body className="text-start p-0">
          <div className="text-center">
            <h5>
              Build updates are available, would you like to reload the page to
              get the updates?
            </h5>
            <p className="mb-1">
              <b>Old Build Version:</b> {oldBuildValue}
            </p>
            <p className="mb-1">
              <b>New Build Version:</b> {currentBuildValue}
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-block">
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-primary btn-md font-weight-medium"
              onClick={async () => {
                window.location.reload();
              }}
            >
              Yes
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-md font-weight-medium"
              onClick={() => {
                setShowReloadModal(false);
                setShowNoReloadModal(true);
                console.debug("No popup-ShowNoReloadModal", showNoReloadModal);
              }}
            >
              No
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        className="modal-md modal-primary preview-detailed-summary"
        show={showNoReloadModal}
        onHide={() => setShowNoReloadModal(false)}
        backdrop="static"
        centered
        animation={false}
        backdropClassName="question-modal"
      >
        <Modal.Title className="modal-title modal-header mt-0 mb-3 summary-border-header">
          <Row className="w-100 pb-2">
            <Col className="col-12 text-center">
              <h3>
                <b>Attention !!!</b>
              </h3>
            </Col>
          </Row>
        </Modal.Title>
        <Modal.Body className="text-start p-0">
          <div className="text-center">
            <h5>After 10mins, the page gets auto reload</h5>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-block">
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-primary btn-md font-weight-medium"
              onClick={() => {
                setShowUiTimerModal(true);
                setShowReloadModal(false);
                setShowNoReloadModal(false);
              }}
            >
              Ok
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default App;
